import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { bilingualLines } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { adminPicksApi, type AdminPickRow } from '../../services/adminPicksApi';

type Tab = 'ALL' | 'PUBLISHED' | 'DRAFT' | 'ARCHIVED';
const TABS: Tab[] = ['ALL', 'PUBLISHED', 'DRAFT', 'ARCHIVED'];
const TAB_LABEL: Record<Tab, string> = {
  ALL: 'All',
  PUBLISHED: 'Published',
  DRAFT: 'Draft',
  ARCHIVED: 'Archived',
};
const STATUS_BADGE: Record<AdminPickRow['status'], string> = {
  PUBLISHED: 'bg-green-100 text-green-800',
  DRAFT: 'bg-yellow-100 text-yellow-800',
  ARCHIVED: 'bg-gray-100 text-gray-600',
};

/** /admin/picks (PRD §5.8): every Pick with recommendations, clicks and saves. */
export default function AdminPicks() {
  const siteLanguage = useSiteLanguage();
  const [tab, setTab] = useState<Tab>('ALL');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<AdminPickRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(
        await adminPicksApi.list({ status: tab === 'ALL' ? undefined : tab, q: query || undefined })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load picks');
    } finally {
      setLoading(false);
    }
  }, [tab, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = async (row: AdminPickRow, status: AdminPickRow['status']) => {
    setBusyId(row.id);
    setError(null);
    try {
      await adminPicksApi.setStatus(row.id, status);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const ages = (r: AdminPickRow) =>
    r.ageMin >= 18 ? 'Adults' : r.ageMax >= 99 ? `${r.ageMin}+` : `${r.ageMin} to ${r.ageMax}`;

  return (
    <AdminLayout
      title="YOYO Picks"
      headerRight={
        <div className="flex gap-2">
          <Link
            to="/admin/picks/submissions"
            className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            Submissions
          </Link>
          <Link
            to="/admin/picks/candidates"
            className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            Candidates
          </Link>
          <Link
            to="/admin/picks/new"
            className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary-dark"
          >
            New pick
          </Link>
        </div>
      }
    >
      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <nav className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                tab === t ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </nav>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title or provider"
          className="min-w-[220px] flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
        />
      </div>

      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-600">No picks.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Pick</th>
                <th className="px-4 py-3">Ages</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Recommend</th>
                <th className="px-4 py-3 text-right">Link clicks 30d</th>
                <th className="px-4 py-3 text-right">Calendar 30d</th>
                <th className="px-4 py-3 text-right">Saved</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/picks/${r.id}`}
                      className="font-semibold text-gray-900 hover:text-primary"
                    >
                      {bilingualLines(r.titleCN, r.titleEN, siteLanguage)
                        .filter(Boolean)
                        .join(' / ')}
                    </Link>
                    <div className="text-xs text-gray-500">
                      {r.providerName} · {r.type === 'EVENT' ? 'Event' : 'Provider'}
                      {r.isFeatured ? ' · Featured' : ''}
                      {r.order !== null ? ` · Order ${r.order}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{ages(r)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[r.status]}`}>
                      {r.status === 'PUBLISHED' && r.ended ? 'Ended' : TAB_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {r.recommendCount} / {r.notRecommendCount}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{r.linkClicks30d}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{r.calendarClicks30d}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{r.favorites}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    {r.status === 'PUBLISHED' ? (
                      <button
                        disabled={busyId === r.id}
                        onClick={() => void setStatus(r, 'ARCHIVED')}
                        className="text-sm text-gray-600 hover:underline disabled:opacity-40"
                      >
                        Archive
                      </button>
                    ) : (
                      <button
                        disabled={busyId === r.id}
                        onClick={() => void setStatus(r, 'PUBLISHED')}
                        className="text-sm text-primary hover:underline disabled:opacity-40"
                      >
                        Publish
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
